import { useState } from "react";
import { Form, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { ReactComponent as DeleteIcon } from "@/assets/images/desktop/closemin.svg";

import { decimalValidateNew, truncateDecimals } from "@/services/Utils";

import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";
import { customStyles, customSelectProductStyles } from "@/components/ReactSelect/NoOptions.styles";
import ProductAlternatives from "@/pages/Planning/OrderApplication/Create/ProductAlternatives/ProductAlternatives.jsx";
import CustomProductName from "@/components/desktop/products/product_name/CustomProductName";
import {
	CustomProductSelectMenu,
	CustomProductSelectOption,
	CustomProductSelectSingleValue,
} from "@/components/desktop/products/custom_product_select/CustomProductSelect";
import { customProductFilterOption } from "@/components/desktop/products/custom_product_select/filterUtils";

import classes from "./createOrderApplication.module.scss";

const getColumnsConfig = (applicationOrderTypeId) => {
	const baseColumns = {
		1: ["objective", "product", "protectionDays", "lackDays", "doseX100L", "doseHa", "doseMachinery", "quantityMachinery", "totalExpense", "actions"],
		2: ["objective", "product", "doseHa", "totalExpense", "actions"],
		3: ["objective", "product", "protectionDays", "lackDays", "doseX100L", "doseHa", "doseMachinery", "actions"],
		4: ["objective", "product", "doseHa", "totalExpense", "actions"],
		5: ["objective", "product", "doseHa", "totalExpense", "actions"],
	};

	return baseColumns[applicationOrderTypeId] || [];
};

const CreateAppOrderTableItem = ({
	data,
	products,
	objectives,
	changeProduct,
	changeObjective,
	changeLackDays,
	changeDoseX100Ml,
	changeDoseHa,
	isCopy = false,
	calculateTotal,
	onRemoveItem = null,
	setData,
	selectMenuPlacement = "top",
}) => {
	const [productText, setProductText] = useState("");
	const [modalParentIndex, setModalParentIndex] = useState(null);
	const selectedEstateId = String(data?.estate_id || "").trim();
	const availableObjectives = (objectives || []).filter((objective) => {
		const estateSource = Array.isArray(objective?.estate_id) ? objective.estate_id.join(",") : objective?.estate_id;
		const estateIds = String(estateSource || "")
			.split(",")
			.map((estateId) => estateId.trim())
			.filter(Boolean);

		if (!selectedEstateId) {
			return estateIds.length === 0;
		}

		return estateIds.length === 0 || estateIds.includes(selectedEstateId);
	});

	let itemsShow = data && data.items ? data.items : [];
	if (data && data.items && data.machinery_integer) {
		if (isCopy) {
			itemsShow = data.items.filter((item) => item.isCopy);
		} else {
			itemsShow = data.items.filter((item) => !item.isCopy);
		}
	}

	const columnRenderers = {
		objective: (item, i) => (
			<td key={`objective-${i}`} style={{ width: "15%" }}>
				<CreatableSelect
					value={objectives?.filter((ob) => String(ob?.value) === String(item.application_orders_objetives_id))}
					options={availableObjectives}
					isClearable
					menuPortalTarget={document.body}
					menuPlacement={selectMenuPlacement}
					formatCreateLabel={(value) => `Crear ${value}`}
					placeholder={<span style={{ fontSize: "12px" }}>Seleccione</span>}
					onChange={(opt, meta) => changeObjective(i, opt, meta)}
					components={{ NoOptionsMessage }}
					styles={customStyles}
					isDisabled={isCopy}
				/>
			</td>
		),
		product: (item, i) => (
			<td key={`product-${i}`} style={{ width: "25%" }}>
				<div className={classes.productRow}>
					<div className={classes.topRow}>
						<div className={classes.selectWrap} style={{ width: item?.product_id ? "85%" : "100%" }}>
							<Select
								className={classes.productSelect}
								value={item && products.find((p) => Number(p?.value) === Number(item.product_id))}
								placeholder=""
								isClearable
								options={productText.length > 1 ? products : []}
								onChange={(value) => {
									changeProduct(i, value);
									setProductText("");
								}}
								onInputChange={(value) => setProductText(value)}
								filterOption={customProductFilterOption}
								menuPortalTarget={document.body}
								menuPlacement={selectMenuPlacement}
								components={{
									NoOptionsMessage,
									Option: CustomProductSelectOption,
									Menu: CustomProductSelectMenu,
									SingleValue: CustomProductSelectSingleValue,
								}}
								showActiveIngredients={true}
								showStock={true}
								estateId={item?.estate_id ?? data?.estate_id}
								styles={customSelectProductStyles}
								isDisabled={isCopy}
							/>
						</div>

						<div className={classes.iconWrap}>
							<CustomProductName
								product={{
									code: item?.product_code,
									value: item?.product_id,
									label: item?.product_name,
								}}
								showName={false}
							/>
						</div>
					</div>

					{!isCopy && !onRemoveItem && item?.product_id && (
						<div className={classes.optionProduct} onClick={() => setModalParentIndex(i)}>
							<span>{(item.product_options?.length || 0) === 0 ? "Añadir opción" : `+${item.product_options.length} opción, añadir otra`}</span>
						</div>
					)}
				</div>
			</td>
		),

		lackDays: (item, i) => (
			<td key={`lackDays-${i}`} style={{ width: "6%" }}>
				<Form.Control
					className={classes.input}
					type="number"
					value={item && item.lack_days}
					onChange={(e) => changeLackDays(i, e.target.value)}
					onKeyDown={decimalValidateNew}
					disabled={isCopy}
				/>
			</td>
		),
		doseX100L: (item, i) => (
			<td key={`doseX100L-${i}`} style={{ width: "10%" }}>
				<Form.Control
					className={classes.input}
					type="number"
					step="0.01"
					value={item && item.dose_100L}
					onChange={(e) => changeDoseX100Ml(i, e.target.value)}
					onKeyDown={decimalValidateNew}
					disabled={isCopy}
				/>
			</td>
		),
		doseHa: (item, i) => (
			<td key={`doseHa-${i}`} style={{ width: "10%" }}>
				<Form.Control
					className={classes.input}
					type="number"
					step="0.01"
					value={item && item.dose_ha}
					onChange={(e) => changeDoseHa(i, e.target.value)}
					onKeyDown={decimalValidateNew}
					disabled={isCopy}
				/>
			</td>
		),
		doseMachinery: (item, i) => (
			<td key={`doseMachinery-${i}`} style={{ width: "12%" }}>
				<Form.Control className={classes.input} type="number" step="0.01" disabled value={truncateDecimals(item?.dose_machinery, 3)} />
			</td>
		),
		quantityMachinery: (item, i) => (
			<td key={`quantityMachinery-${i}`} style={{ width: "10%" }}>
				<Form.Control className={classes.input} type="number" step="0.01" disabled value={truncateDecimals(item?.quantity_machinery, 3)} />
			</td>
		),
		totalExpense: (item, i) => (
			<td key={`totalExpense-${i}`} style={{ width: "12%" }}>
				<Form.Control className={classes.input} type="number" step="0.01" disabled value={truncateDecimals(item?.quantity, 3)} />
			</td>
		),
		actions: (item, i) => (
			<td key={`actions-${i}`} style={{ width: "5%", textAlign: "center" }}>
				{!isCopy && (
					<OverlayTrigger overlay={<Tooltip>Eliminar Producto</Tooltip>}>
						<DeleteIcon
							className={classes.buttonClose}
							onClick={() => {
								if (onRemoveItem) {
									onRemoveItem(i);
								} else {
									const itemsCopy = [...data.items];
									itemsCopy.splice(i, 1);
									calculateTotal({ newData: { ...data, items: itemsCopy } });
								}
							}}
						/>
					</OverlayTrigger>
				)}
			</td>
		),
	};

	return (
		<>
			<Table className={classes.tableDetail}>
				<tbody className={classes.tableBody}>
					{itemsShow.map((item, i) => {
						const columnsToRender = getColumnsConfig(data.application_order_type_id);
						return <tr key={i}>{columnsToRender.map((column) => (columnRenderers[column] ? columnRenderers[column](item, i) : null))}</tr>;
					})}
				</tbody>
			</Table>

			{modalParentIndex !== null && (
				<ProductAlternatives
					open={true}
					onClose={() => setModalParentIndex(null)}
					data={data}
					setData={setData}
					products={products}
					objectives={objectives}
					parentItem={itemsShow[modalParentIndex]}
					parentItemIndex={modalParentIndex}
				/>
			)}
		</>
	);
};

export default CreateAppOrderTableItem;
